import { useEffect, useState } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  DocumentDuplicateIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/outline";
import {
  createDocument,
  deleteDocument,
  getDocuments,
  updateDocument,
  getDocumentById,
} from "../../api/documentsApi";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import DeleteConfirmationModal from "../../components/deleteModal";
import { useNavigate } from "react-router-dom";
import DocumentAddModal from "./DocumentAddModal";
import PdfTemplateEditor from "./PdfTemplateEditor";
import SendForSignModal from "./SendForSignModal";

const DocumentPage = () => {
  const [showAddModal, setShowAddModal] = useState(false);

  const LIMIT = 10;

  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showEditor, setShowEditor] = useState(false);
  const [createdDocument, setCreatedDocument] = useState(null);

  const [editorDocument, setEditorDocument] = useState(null);

  const [deleteDocumentId, setDeleteDocumentId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingDocument, setEditingDocument] = useState(null);
  const [saving, setSaving] = useState(false);

  const [sendForSignDoc, setSendForSignDoc] = useState(null);

  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const handleDocumentSubmit = async (info) => {
    // try {
    //   setSaving(true);
    //   const payload = {
    //     name: info.name,
    //     reg_no: info.reg_no,
    //     vat_no: info.vat_no,
    //     accountant_name: info.accountant_name,
    //     accountant_phone: info.accountant_phone,
    //     company_id: info.company_id,
    //     euid: info.euid,
    //   };
    //   if (editingDocument) {
    //     await updateDocument(editingDocument.id, payload);
    //   } else {
    //     await createDocument(payload);
    //   }
    //   setEditingDocument(null);
    //   setShowAddModal(false);
    //   // Refresh list
    //   const offset = (page - 1) * LIMIT;
    //   const data = await getDocuments(LIMIT, offset);
    //   setDocuments(data.results || []);
    //   setTotal(data.count || 0);
    // } catch (err) {
    //   setToast({
    //     type: "error",
    //     message:
    //       err?.response?.data?.detail || err?.detail || "Something went wrong",
    //   });
    // } finally {
    //   setSaving(false);
    // }
  };

  const handleSendForSign = (doc) => {
    setSendForSignDoc(doc);
  };

  const handleEdit = (doc) => {
    setEditingDocument(doc);
    setShowAddModal(true);
  };

  const handleOpenEditor = async (doc) => {
    try {
      const fullDoc = await getDocumentById(doc.id);
      setEditorDocument(fullDoc);
    } catch (err) {
      setToast({
        type: "error",
        message: "Failed to load document.",
      });
    }
  };

  const handleDocumentDelete = async () => {
    try {
      setDeleting(true);
      await deleteDocument(deleteDocumentId);
      setDeleteDocumentId(null);
      // Refresh list
      const offset = (page - 1) * LIMIT;
      const data = await getDocuments(LIMIT, offset);
      setDocuments(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message: "Failed to delete document.",
      });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setError("");

        const offset = (page - 1) * LIMIT;
        const data = await getDocuments(LIMIT, offset);

        setDocuments(data.results || []);
        setTotal(data.count || 0);
      } catch (err) {
        setError("Failed to load documents.");
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Documents</h1>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Create Document
        </button>
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4 text-center">Document Name</th>
                <th className="px-6 py-4 text-center">Document Type</th>
                <th className="px-6 py-4 text-center">Status</th>
                <th className="px-6 py-4 text-center">
                  Issued / Signed / Not Signed
                </th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {documents.map((document) => (
                <tr
                  key={document.id}
                  className="border-b text-center border-white/5 last:border-none hover:bg-slate-800/40 transition"
                >
                  <td className="px-6 py-4 text-sm text-slate-300">
                    {document.title}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {document.type}
                  </td>

                  <td className="px-6 py-4 ">
                    <div className="flex items-center justify-center space-x-4">
                      <button
                        id={`toggle-${document.id}`}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          document.is_active ? "bg-pink-600" : "bg-gray-600"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            document.is_active
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </td>

                  <td
                    onClick={() => navigate(`/dashboard/all-documents`)}
                    className="px-6 py-4 underline underline-offset-4 decoration-pink-600 text-sm text-white"
                  >
                    {document.generated_document_counts}
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-center items-center gap-3">
                      {/* Edit */}
                      <button
                        onClick={() => handleEdit(document)}
                        className="group relative text-slate-400 hover:text-pink-500 transition"
                      >
                        <PencilIcon className="w-5 h-5" />
                        <span
                          className="absolute -top-8 left-1/2 -translate-x-1/2 
        bg-slate-800 text-xs text-white px-2 py-1 rounded 
        opacity-0 group-hover:opacity-100 transition whitespace-nowrap"
                        >
                          Edit
                        </span>
                      </button>

                      {/* Editor */}
                      <button
                        onClick={() => handleOpenEditor(document)}
                        className="group relative text-slate-400 hover:text-pink-500 transition"
                      >
                        <DocumentDuplicateIcon className="w-5 h-5" />
                        <span
                          className="absolute -top-8 left-1/2 -translate-x-1/2 
        bg-slate-800 text-xs text-white px-2 py-1 rounded 
        opacity-0 group-hover:opacity-100 transition whitespace-nowrap"
                        >
                          Open Editor
                        </span>
                      </button>

                      {/* Send for Sign */}
                      <button
                        onClick={() => handleSendForSign(document)}
                        className="group relative text-slate-400 hover:text-pink-500 transition"
                      >
                        <PaperAirplaneIcon className="w-5 h-5" />
                        <span
                          className="absolute -top-8 left-1/2 -translate-x-1/2 
        bg-slate-800 text-xs text-white px-2 py-1 rounded 
        opacity-0 group-hover:opacity-100 transition whitespace-nowrap"
                        >
                          Send for Sign
                        </span>
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => setDeleteDocumentId(document.id)}
                        className="group relative text-slate-400 hover:text-red-500 transition"
                      >
                        <TrashIcon className="w-5 h-5" />
                        <span
                          className="absolute -top-8 left-1/2 -translate-x-1/2 
        bg-slate-800 text-xs text-white px-2 py-1 rounded 
        opacity-0 group-hover:opacity-100 transition whitespace-nowrap"
                        >
                          Delete
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state placeholder (future) */}
        {documents.length === 0 && (
          <div className="p-8 text-center text-slate-400">
            No documents found.
          </div>
        )}
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* <DocumentAddModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
        }}
        onSubmit={handleDocumentSubmit}
        loading={saving}
      /> */}

      <DocumentAddModal
        isOpen={showAddModal}
        initialData={editingDocument}
        onClose={() => {
          setShowAddModal(false);
          setEditingDocument(null);
        }}
        onPdfEditor={(doc) => {
          setCreatedDocument(doc);
          setShowEditor(true);
        }}
        onSuccess={async () => {
          const offset = (page - 1) * LIMIT;
          const data = await getDocuments(LIMIT, offset);
          setDocuments(data.results || []);
          setTotal(data.count || 0);
        }}
      />

      <SendForSignModal
        isOpen={!!sendForSignDoc}
        documentId={sendForSignDoc?.id}
        onClose={() => setSendForSignDoc(null)}
        onSuccess={async () => {
          const offset = (page - 1) * LIMIT;
          const data = await getDocuments(LIMIT, offset);
          setDocuments(data.results || []);
          setTotal(data.count || 0);
        }}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteDocumentId}
        description="Are you sure you want to delete this document?  This action cannot be undone."
        onClose={() => setDeleteDocumentId(null)}
        onConfirm={handleDocumentDelete}
        loading={deleting}
      />

      {showEditor && (
        <PdfTemplateEditor
          file={createdDocument.file}
          documentId={createdDocument.id}
          onClose={() => setShowEditor(false)}
          onSave={async (fields) => {
            try {
              const formatted = fields.map((field) => ({
                field: field.type,
                id: field.id,
                x: Math.round(field.x),
                y: Math.round(field.y),
                page: field.page,
              }));

              await updateDocument(createdDocument.id, {
                placeholder_fields: formatted,
              });

              setShowEditor(false);
              const offset = (page - 1) * LIMIT;
              const data = await getDocuments(LIMIT, offset);
              setDocuments(data.results || []);
              setTotal(data.count || 0);
            } catch (err) {
              console.error(err.response?.data);
            }
          }}
        />
      )}

      {editorDocument && (
        <PdfTemplateEditor
          file={editorDocument.file}
          existingFields={editorDocument.placeholder_fields}
          documentId={editorDocument.id}
          onClose={() => setEditorDocument(null)}
          onSave={async (fields) => {
            try {

              const formatted = fields.map((field) => ({
                field: field.type,
                id: field.id,
                x: Math.round(field.x),
                y: Math.round(field.y),
                page: field.page,
              }));

              await updateDocument(editorDocument.id, {
                placeholder_fields: formatted,
              });

              setEditorDocument(null);

              // Refresh list
              const offset = (page - 1) * LIMIT;
              const data = await getDocuments(LIMIT, offset);
              setDocuments(data.results || []);
              setTotal(data.count || 0);
            } catch (err) {
              setToast({
                type: "error",
                message: "Failed to update template.",
              });
            }
          }}
        />
      )}

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default DocumentPage;
